import { supabase } from '../lib/supabaseClient';
import type {
  DeliveryAttempt,
  NotificationAuditEvent,
  NotificationCategory,
  NotificationKillSwitch,
  NotificationOutboxRow,
  NotificationPriority,
  NotificationTemplate,
  RecipientRouteMasked,
  UUID,
} from '../types/domain';

/**
 * Notifications / WhatsApp (FASE 13, migration 0052/0053). Every write below
 * calls a permission-gated `_checked` RPC — never a direct `.insert()`/
 * `.update()`. All 5 tables carry RESTRICTIVE `false` INSERT/UPDATE/DELETE
 * RLS (proved live: even a TENANT_OWNER cannot raw-PATCH
 * notification_kill_switch — only the audited RPC can change it).
 *
 * PROVIDER STATUS: every send in this build goes through the real internal
 * SANDBOX_MOCK_PROVIDER (deterministic, no network call, never contacts a
 * production recipient) — classified SANDBOX_PROVEN. There is no real
 * WhatsApp Business API credential wired up; production delivery is
 * BLOCKED_BY_PROVIDER until one exists. Nothing here ever claims a message
 * was actually delivered to a real phone.
 */

// ---------------------------------------------------------------------------
// Notification requests (outbox — idempotent)
// ---------------------------------------------------------------------------

/**
 * idempotencyKey: generate ONCE per logical "send this notification" action
 * (e.g. once per report-ready event, once per button click) and reuse the
 * SAME key across a retry of that exact action — the server deterministically
 * resolves a retry to the already-created canonical notification_outbox row,
 * never a duplicate. A genuinely new, intentional send must always use a
 * fresh key.
 */
export async function requestNotification(params: {
  branchId: UUID | null;
  notificationType: string;
  category: NotificationCategory;
  templateId: UUID;
  routeId: UUID;
  payload: Record<string, unknown>;
  sourceReportRunId?: UUID | null;
  priority?: NotificationPriority;
  idempotencyKey: UUID;
}): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_request_notification_checked', {
    p_branch_id: params.branchId,
    p_notification_type: params.notificationType,
    p_category: params.category,
    p_template_id: params.templateId,
    p_route_id: params.routeId,
    p_payload: params.payload,
    p_source_report_run_id: params.sourceReportRunId ?? null,
    p_priority: params.priority ?? 'NORMAL',
    p_idempotency_key: params.idempotencyKey,
  });
  if (error) throw new Error(`fn_request_notification_checked failed: ${error.message}`);
  return data as UUID;
}

/**
 * Manually triggers the next delivery attempt for a notification still in
 * REQUESTED state. There is no cron/worker in this build (BLOCKED_BY_
 * INFRASTRUCTURE — see closure report) — a queued retry only actually runs
 * when an authorized operator (or a scheduled job in a future infra) calls
 * this. Bounded to 3 attempts server-side; the 3rd non-success transitions
 * the notification to DEAD_LETTER automatically.
 */
export async function retryNotification(notificationId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_retry_notification_checked', { p_notification_id: notificationId });
  if (error) throw new Error(`fn_retry_notification_checked failed: ${error.message}`);
}

export async function listNotifications(): Promise<NotificationOutboxRow[]> {
  const { data, error } = await supabase
    .from('notification_outbox')
    .select(
      'notification_id, tenant_id, branch_id, notification_type, category, template_id, route_id, idempotency_key, payload, source_report_run_id, priority, status, requested_by, requested_at, scheduled_at, processed_at, attempt_count, last_error, next_retry_at, dead_letter_reason'
    )
    .order('requested_at', { ascending: false })
    .limit(200);
  if (error) throw new Error(`listNotifications failed: ${error.message}`);
  return (data ?? []) as NotificationOutboxRow[];
}

export async function listDeliveryAttempts(notificationId: UUID): Promise<DeliveryAttempt[]> {
  const { data, error } = await supabase
    .from('delivery_attempts')
    .select('attempt_id, tenant_id, notification_id, attempt_no, channel, provider, started_at, finished_at, result, provider_response, error_code, retryable, request_id')
    .eq('notification_id', notificationId)
    .order('attempt_no', { ascending: true });
  if (error) throw new Error(`listDeliveryAttempts failed: ${error.message}`);
  return (data ?? []) as DeliveryAttempt[];
}

export async function getNotificationAuditTrail(notificationId: UUID): Promise<NotificationAuditEvent[]> {
  const { data, error } = await supabase.rpc('fn_get_notification_audit_trail_checked', { p_notification_id: notificationId });
  if (error) throw new Error(`fn_get_notification_audit_trail_checked failed: ${error.message}`);
  return (data ?? []) as NotificationAuditEvent[];
}

// ---------------------------------------------------------------------------
// Recipient routes (masked read — raw table SELECT is route_manage-only)
// ---------------------------------------------------------------------------

export async function listRecipientRoutes(): Promise<RecipientRouteMasked[]> {
  const { data, error } = await supabase.rpc('fn_list_recipient_routes_checked');
  if (error) throw new Error(`fn_list_recipient_routes_checked failed: ${error.message}`);
  return (data ?? []) as RecipientRouteMasked[];
}

/** notification.route_manage only. routeId omitted => create; supplied => update in place. */
export async function upsertRecipientRoute(params: {
  routeId?: UUID | null;
  branchId: UUID | null;
  roleCode?: string | null;
  userId?: UUID | null;
  customerId?: UUID | null;
  channel: 'WHATSAPP' | 'EMAIL';
  addressRef: string;
  maskingPolicy: 'FULL' | 'SUMMARY_ONLY' | 'MASKED';
  priority?: number;
  enabled: boolean;
}): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_upsert_recipient_route_checked', {
    p_route_id: params.routeId ?? null,
    p_branch_id: params.branchId,
    p_role_code: params.roleCode ?? null,
    p_user_id: params.userId ?? null,
    p_customer_id: params.customerId ?? null,
    p_channel: params.channel,
    p_address_ref: params.addressRef,
    p_masking_policy: params.maskingPolicy,
    p_priority: params.priority ?? 100,
    p_enabled: params.enabled,
  });
  if (error) throw new Error(`fn_upsert_recipient_route_checked failed: ${error.message}`);
  return data as UUID;
}

// ---------------------------------------------------------------------------
// Templates (SCD-2 versioned — notification.template_manage only)
// ---------------------------------------------------------------------------

export async function listTemplates(): Promise<NotificationTemplate[]> {
  const { data, error } = await supabase
    .from('notification_templates')
    .select('template_id, tenant_id, template_code, channel, event_type, version, status, body, variables, language, created_by, created_at, effective_from, effective_to')
    .order('template_code', { ascending: true })
    .order('version', { ascending: false });
  if (error) throw new Error(`listTemplates failed: ${error.message}`);
  return (data ?? []) as NotificationTemplate[];
}

export async function createTemplateVersion(params: {
  templateCode: string;
  channel: 'WHATSAPP' | 'EMAIL';
  eventType: string;
  body: string;
  variables: Array<{ name: string; sensitive: boolean }>;
  language?: string;
}): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_template_version_checked', {
    p_template_code: params.templateCode,
    p_channel: params.channel,
    p_event_type: params.eventType,
    p_body: params.body,
    p_variables: params.variables,
    p_language: params.language ?? 'id',
  });
  if (error) throw new Error(`fn_create_template_version_checked failed: ${error.message}`);
  return data as UUID;
}

// ---------------------------------------------------------------------------
// Outbound kill switch (server-authoritative, audited, tenant-wide only)
// ---------------------------------------------------------------------------

export async function getKillSwitch(): Promise<NotificationKillSwitch | null> {
  const { data, error } = await supabase.from('notification_kill_switch').select('tenant_id, enabled, changed_by, changed_at').maybeSingle();
  if (error) throw new Error(`getKillSwitch failed: ${error.message}`);
  return data as NotificationKillSwitch | null;
}

/** notification.kill_switch_manage only (TENANT_OWNER/PLATFORM_OWNER/SYSTEM_ADMIN) — proved live that a MANAGER is denied. */
export async function setKillSwitch(enabled: boolean): Promise<void> {
  const { error } = await supabase.rpc('fn_set_kill_switch_checked', { p_enabled: enabled });
  if (error) throw new Error(`fn_set_kill_switch_checked failed: ${error.message}`);
}
